import { CheckIcon, MinusIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import {
  type CheckboxProps as AriaCheckboxProps,
  Checkbox,
  CheckboxGroup,
  type CheckboxGroupProps,
  composeRenderProps,
} from 'react-aria-components';
import { composeTailwindRenderProps } from '../../utils';
import { Text, TextSize } from '../text';

function _CheckboxGroup(props: CheckboxGroupProps) {
  const { className, children, ...other } = props;

  return (
    <CheckboxGroup {...other}>
      {composeRenderProps(children, (children) => (
        <div className={cx('flex flex-col gap-4', className)}>{children}</div>
      ))}
    </CheckboxGroup>
  );
}

interface CheckboxProps extends AriaCheckboxProps {
  /**
   * Whether a long label wraps onto more lines, with the box beside its first line, instead of being cut to one
   * line with an ellipsis; for sentence-long labels such as an acknowledgement
   *
   * @optional
   * @default false
   */
  wrapLabel?: boolean;
}

function _Checkbox(props: CheckboxProps) {
  const { className, children: childrenProp, wrapLabel = false, ...other } = props;

  return (
    <Checkbox
      className={composeTailwindRenderProps(
        className,
        'group flex cursor-pointer items-center gap-2 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
      )}
      {...other}
    >
      {composeRenderProps(childrenProp, (children, { isIndeterminate, isSelected }) => (
        <div className={cx('flex gap-4', wrapLabel ? 'items-start' : 'items-center')}>
          <div
            className={cx(
              // Base styles
              'inline-flex w-10 h-10 shrink-0 items-center justify-center border border-gray-utility-300 bg-white transition-all',
              // Indeterminate state
              'group-data-[indeterminate]:border-brand-600 group-data-[indeterminate]:bg-brand-600 group-data-[indeterminate]:text-white',
              // Selected state
              'group-data-[selected]:border-brand-600 group-data-[selected]:bg-brand-600 group-data-[selected]:text-white',
              // Readonly state
              'group-data-[readonly]:border-none group-data-[readonly]:bg-transparent group-data-[readonly]:text-brand-600',
              // Invalid state
              'group-data-[invalid]:border-error-primary group-data-[invalid]:bg-error-primary group-data-[invalid]:text-white',
              // Focus state
              'group-data-[focus-visible]:outline-none group-data-[focus-visible]:ring-2 group-data-[focus-visible]:ring-brand-600 group-data-[focus-visible]:ring-offset-2',
              // Disabled state
              'group-data-[disabled]:border-disabled group-data-[disabled]:bg-primary-disabled group-data-[disabled]:text-disabled',
              // Hover state
              'group-hover:border-brand-500 group-data-[disabled]:group-hover:border-gray-utility-300',
            )}
          >
            {isIndeterminate && <MinusIcon className="h-3 w-3" />}
            {isSelected && !isIndeterminate && <CheckIcon className="h-3 w-3" />}
          </div>
          {children && (
            <Text size={TextSize.Sm} showEllipsis={!wrapLabel}>
              {children}
            </Text>
          )}
        </div>
      ))}
    </Checkbox>
  );
}

export { _Checkbox as Checkbox, _CheckboxGroup as CheckboxGroup };
export type { CheckboxProps, CheckboxGroupProps };
